/**
 * Run decision rules (server: RunDecisionService + canAcceptShortfall). The console offers only the
 * decisions the server can accept, and says why one is missing:
 *   CORRECTED         the run must balance now (after corrections / re-check)
 *   ACCEPT_SHORTFALL  only RESIDUAL / YIELD / EXACT_ZERO with left over ≥ 0, never for MIXING
 *   MOVE_TO_CONV      always possible; never reversible
 */
import type { RunBalance, RunCause, RunDecision } from '../../api/process'

export interface DecisionOption {
  value: RunDecision
  allowed: boolean
  /** Why it is not offered. */
  reason: string | null
}

export function decisionOptions(balance: Pick<RunBalance, 'kinds' | 'residualG' | 'result'>, cause: RunCause | ''): DecisionOption[] {
  const passes = balance.result === 'BALANCED' || (balance.kinds.length === 0 && balance.result !== 'EXCEPTION')
  const surplus = balance.kinds.includes('SURPLUS') || balance.residualG < 0
  const acceptBlock = passes
    ? 'The run balances now: close it as balanced after correction.'
    : surplus
      ? 'More came out than went in: a surplus cannot be accepted.'
      : cause === 'MIXING'
        ? 'Mixing cannot be accepted: correct it or move the outputs to ordinary.'
        : !balance.kinds.every((k) => ['RESIDUAL', 'YIELD', 'EXACT_ZERO'].includes(k))
          ? 'This problem cannot be accepted as a shortfall.'
          : null
  return [
    {
      value: 'CORRECTED',
      allowed: passes,
      reason: passes ? null : 'The run still does not balance: record a correction and re-check it first.',
    },
    { value: 'ACCEPT_SHORTFALL', allowed: acceptBlock === null, reason: acceptBlock },
    { value: 'MOVE_TO_CONV', allowed: true, reason: null },
  ]
}

export interface DecisionDraft {
  cause: RunCause | ''
  decision: RunDecision | ''
  note: string
}
export type DecisionProblems = Partial<Record<keyof DecisionDraft, string>>

export function validateDecision(d: DecisionDraft, options: DecisionOption[]): DecisionProblems {
  const p: DecisionProblems = {}
  if (!d.cause) p.cause = 'Choose the cause found in the investigation'
  if (!d.decision) p.decision = 'Choose how the run closes'
  else {
    const o = options.find((x) => x.value === d.decision)
    if (o && !o.allowed) p.decision = o.reason ?? 'Not possible for this run'
  }
  if (d.note.trim().length > 500) p.note = 'At most 500 characters'
  if (d.decision === 'ACCEPT_SHORTFALL' && d.note.trim().length < 3) p.note = 'Write the reason the shortfall is accepted'
  return p
}
export const decisionOk = (p: DecisionProblems) => Object.values(p).every((v) => !v)

/** Who may decide or correct: not the run's operator nor the cleanout person (server 403). */
export function ownRunReason(userId: string | null | undefined, run: { operator: { id: string } | null; cleanoutBy: { id: string } | null }): string | null {
  if (!userId) return null
  if (run.operator?.id === userId) return 'You ran this machine run: another supervisor investigates and decides it.'
  if (run.cleanoutBy?.id === userId) return 'You did the cleanout for this run: another supervisor investigates and decides it.'
  return null
}
